import {
  Badge,
  BodyText,
  Collapsible,
  HeaderText,
  Skeleton,
} from "@/components/ui";
import { formatDateTime } from "@/lib/time";
import { getResourceType } from "../constants";
import { useResourceLookup } from "../network/useAdminFhir";
import {
  type FhirResource,
  formatFhirValue,
  getCreatedSource,
} from "../utils/fhir";
import { JsonPretty } from "./JsonPretty";

interface ResourceDetailProps {
  resourceType: string;
  resourceId: string;
}

// Meta / structural keys we don't surface in the human summary.
const HIDDEN_KEYS = new Set(["resourceType", "id", "meta", "text"]);

/** Current value of the selected resource: header, field summary, raw JSON. */
export const ResourceDetail = ({
  resourceType,
  resourceId,
}: ResourceDetailProps) => {
  const {
    data: resource,
    isLoading,
    isError,
    error,
  } = useResourceLookup(resourceType, resourceId);
  const label = getResourceType(resourceType)?.label ?? resourceType;

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  if (isError || !resource) {
    return (
      <BodyText textColor="error">
        Could not load {label}/{resourceId}
        {error?.response?.status ? ` (${error.response.status})` : ""}.
      </BodyText>
    );
  }

  const createdSource = getCreatedSource(resource);
  const summaryFields = Object.entries(resource).filter(
    ([key, value]) =>
      !HIDDEN_KEYS.has(key) && value !== undefined && value !== null,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <HeaderText size="lg">{label}</HeaderText>
        <span className="font-mono text-xs text-secondary">{resource.id}</span>
        {resource.meta?.versionId && (
          <Badge variant="info">v{resource.meta.versionId}</Badge>
        )}
        {createdSource && (
          <Badge variant="light">source: {createdSource}</Badge>
        )}
      </div>

      {resource.meta?.lastUpdated && (
        <BodyText size="sm" textColor="secondary">
          Last updated {formatDateTime(resource.meta.lastUpdated)}
        </BodyText>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {summaryFields.map(([key, value]) => {
          const isNested = typeof value === "object" && value !== null;
          return (
            <div
              key={key}
              className="flex h-56 min-w-0 flex-col overflow-hidden rounded-md border"
            >
              <div className="border-b bg-muted/40 px-3 py-1.5">
                <BodyText size="xs" textColor="secondary" bold>
                  {key}
                </BodyText>
              </div>
              <div className="min-h-0 flex-1 overflow-auto p-3">
                {isNested ? (
                  <JsonPretty value={value} fill />
                ) : (
                  <BodyText size="sm" className="break-words">
                    {formatFhirValue(value)}
                  </BodyText>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Collapsible title="Raw JSON">
        <JsonPretty value={resource as FhirResource} className="mt-2" />
      </Collapsible>
    </div>
  );
};
